import { useState, useEffect, useRef } from 'react';
import { AudioFile } from '../types';

// Module-level storage for progress start times by file ID
const progressStartTimes = new Map<string, number>();

interface ProgressData {
  percentage: number;
  elapsedSeconds: number;
  estimatedTotalSeconds: number;
  remainingSeconds: number;
  isOverdue: boolean;
}

export const useTranscriptionProgress = (
  audioFile: AudioFile
): ProgressData | null => {
  const [progress, setProgress] = useState<ProgressData | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);

  useEffect(() => {
    // Clear interval and reset if not processing
    if (audioFile.status !== 'processing' || !audioFile.processingEstimation) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      setProgress(null);

      // Clean up stored start time when transcription is no longer processing
      progressStartTimes.delete(audioFile.id);
      return;
    }

    // Get initial data from backend
    const estimation = audioFile.processingEstimation;
    const initialElapsed = estimation.elapsedSeconds;
    const totalSeconds = estimation.estimatedTotalSeconds;

    // Use stored progress start time, or calculate new one
    const storedStartTime = progressStartTimes.get(audioFile.id);

    if (storedStartTime) {
      // Use the stored start time
      startTimeRef.current = storedStartTime;
    } else {
      // Calculate start time for the first time
      const calculatedStartTime = Date.now() - initialElapsed * 1000;
      startTimeRef.current = calculatedStartTime;

      // Store the start time for this file
      progressStartTimes.set(audioFile.id, calculatedStartTime);
    }

    const calculateProgress = () => {
      // Calculate elapsed time since the transcription started
      const now = Date.now();
      const elapsedMs = now - startTimeRef.current;
      const elapsedSeconds = Math.floor(elapsedMs / 1000);

      // Calculate percentage (cap at 95% to avoid showing 100% before actual completion)
      const percentage = Math.min(
        95,
        Math.floor((elapsedSeconds / totalSeconds) * 100)
      );

      // Calculate remaining time
      const remainingSeconds = Math.max(0, totalSeconds - elapsedSeconds);

      // Check if we're overdue (taking longer than estimated)
      const isOverdue = elapsedSeconds > totalSeconds;

      setProgress({
        percentage,
        elapsedSeconds,
        estimatedTotalSeconds: totalSeconds,
        remainingSeconds,
        isOverdue,
      });
    };

    // Calculate immediately using the same logic as the interval function
    calculateProgress();

    // Update every second
    intervalRef.current = setInterval(calculateProgress, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [audioFile.id, audioFile.status, audioFile.processingEstimation]);

  return progress;
};
